/**
 * Zustand store for Server-Sent Events (SSE) state management.
 *
 * Handles:
 * - SSE connection status
 * - Active job tracking (job_id → projectId mapping)
 * - Event storage grouped by project
 * - localStorage persistence with TTL
 */

import { create } from 'zustand';

import type { ActiveJob, SSEConnectionStatus, SSEEvent, StoredSSEData } from '~/types';

import { logger } from '../utils/logger';
import { getWithTTL, setWithTTL } from '../utils/storage';

const STORAGE_KEY = 'sse_data';
const EVENTS_TTL = 24 * 60 * 60 * 1000; // 24 hours
const MAX_EVENTS_PER_PROJECT = 100;

interface SSEStore {
  // State
  connectionStatus: SSEConnectionStatus;
  activeJobs: Record<string, ActiveJob>; // jobId → ActiveJob
  eventsByProject: Record<string, SSEEvent[]>; // projectId → events

  // Connection Actions
  setConnectionStatus: (status: SSEConnectionStatus) => void;

  // Active Job Actions
  addActiveJob: (jobId: string, projectId: string, estimatedDuration: number) => void;
  removeActiveJob: (jobId: string) => void;
  updateJobPhase: (jobId: string, phase: string) => void;
  updateJobProgress: (jobId: string, progress: number) => void;
  updateJobStatus: (jobId: string, status: string) => void;
  getActiveProjectIds: () => string[];
  clearAllJobs: () => void;

  // Event Actions
  addEvent: (projectId: string, event: SSEEvent) => void;
  clearProjectEvents: (projectId: string) => void;
  clearAllEvents: () => void;

  // Persistence
  loadFromStorage: () => void;
  saveToStorage: () => void;
}

/**
 * Remove duplicate events by ID.
 * Keeps the first occurrence of each event ID.
 */
function deduplicateEvents(events: SSEEvent[]): SSEEvent[] {
  const seen = new Set<string>();
  return events.filter(event => {
    if (seen.has(event.id)) {
      return false;
    }
    seen.add(event.id);
    return true;
  });
}

/**
 * Enforce max events per project (FIFO eviction).
 */
function enforceEventLimit(events: SSEEvent[]): SSEEvent[] {
  if (events.length <= MAX_EVENTS_PER_PROJECT) {
    return events;
  }

  // Keep most recent events
  return events.slice(-MAX_EVENTS_PER_PROJECT);
}

/**
 * Check if an active job is stale (expired).
 *
 * Job is stale if:
 * - Older than 1.5x estimated duration, OR
 * - Older than 30 minutes (hard limit, reduced from 1 hour)
 *
 * Note: PRD review jobs have 3600s estimated duration, so we need a
 * hard limit to prevent zombie jobs from persisting too long.
 */
const MAX_JOB_AGE_MS_STRICT = 30 * 60 * 1000; // 30 minutes hard limit

function isJobStale(job: ActiveJob): boolean {
  const ageMs = Date.now() - job.startedAt;
  const estimatedAgeMs = job.estimatedDuration * 1.5 * 1000; // 1.5x estimate (reduced from 2x)

  return ageMs > Math.min(estimatedAgeMs, MAX_JOB_AGE_MS_STRICT);
}

/**
 * Sort events by timestamp (chronological order).
 * Events may arrive out of order due to async race conditions in the agent service.
 */
function sortEventsByTimestamp(events: SSEEvent[]): SSEEvent[] {
  return [...events].sort((a, b) => {
    const aTime = a.data?.timestamp ? new Date(a.data.timestamp).getTime() : 0;
    const bTime = b.data?.timestamp ? new Date(b.data.timestamp).getTime() : 0;

    return aTime - bTime;
  });
}

export const useSSEStore = create<SSEStore>((set, get) => ({
  // Initial State
  connectionStatus: 'disconnected',
  activeJobs: {},
  eventsByProject: {},

  // Connection Actions
  setConnectionStatus: (status: SSEConnectionStatus): void => {
    set({ connectionStatus: status });
  },

  // Active Job Actions
  addActiveJob: (jobId: string, projectId: string, estimatedDuration: number): void => {
    const job: ActiveJob = {
      projectId,
      startedAt: Date.now(),
      estimatedDuration,
    };

    set(state => ({
      activeJobs: {
        ...state.activeJobs,
        [jobId]: job,
      },
    }));

    // Persist to localStorage
    get().saveToStorage();
  },

  removeActiveJob: (jobId: string): void => {
    set(state => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [jobId]: _removed, ...remainingJobs } = state.activeJobs;
      return { activeJobs: remainingJobs };
    });

    // Persist to localStorage
    get().saveToStorage();
  },

  clearAllJobs: (): void => {
    logger.info({ component: 'sseStore' }, 'Clearing all active jobs (connection reset)');
    set({ activeJobs: {} });

    // Persist to localStorage
    get().saveToStorage();
  },

  updateJobPhase: (jobId: string, phase: string): void => {
    set(state => {
      const job = state.activeJobs[jobId];
      if (!job) return state;

      return {
        activeJobs: {
          ...state.activeJobs,
          [jobId]: {
            ...job,
            currentPhase: phase,
          },
        },
      };
    });

    // Persist to localStorage
    get().saveToStorage();
  },

  updateJobProgress: (jobId: string, progress: number): void => {
    set(state => {
      const job = state.activeJobs[jobId];
      if (!job) return state;

      return {
        activeJobs: {
          ...state.activeJobs,
          [jobId]: {
            ...job,
            progress,
          },
        },
      };
    });

    // Persist to localStorage
    get().saveToStorage();
  },

  updateJobStatus: (jobId: string, status: string): void => {
    set(state => {
      const job = state.activeJobs[jobId];
      if (!job) return state;

      return {
        activeJobs: {
          ...state.activeJobs,
          [jobId]: {
            ...job,
            status,
          },
        },
      };
    });

    // Persist to localStorage
    get().saveToStorage();
  },

  getActiveProjectIds: (): string[] => {
    const { activeJobs } = get();
    return Object.values(activeJobs).map(job => job.projectId);
  },

  // Event Actions
  addEvent: (projectId: string, event: SSEEvent): void => {
    set(state => {
      const existingEvents = state.eventsByProject[projectId] || [];

      // Deduplicate: Skip if event with same ID already exists
      const eventExists = existingEvents.some(e => e.id === event.id);
      if (eventExists) {
        return state; // No change
      }

      // Sort by timestamp to handle out-of-order event arrival (async race conditions)
      // Then deduplicate as safety net (in case race conditions bypass initial check)
      // Then enforce event limit
      let updatedEvents = sortEventsByTimestamp([...existingEvents, event]);
      updatedEvents = deduplicateEvents(updatedEvents);
      updatedEvents = enforceEventLimit(updatedEvents);

      // Find job for this project (if any)
      const jobEntry = Object.entries(state.activeJobs).find(
        ([_, job]) => job.projectId === projectId,
      );
      const [jobId, job] = jobEntry || [null, null];

      // Update job tracking from event data
      let updatedJobs = state.activeJobs;
      if (jobId && job) {
        const updatedJob = { ...job };

        // Update phase from events that contain phase info
        if ('phase' in event.data && event.data.phase) {
          updatedJob.currentPhase = event.data.phase;
        }

        // Update progress from progress events
        if ('progress' in event.data && typeof event.data.progress === 'number') {
          updatedJob.progress = event.data.progress;
        }

        // Update status on completion/failure/cancellation
        if (event.event === 'completed') {
          updatedJob.status = 'COMPLETED';
        } else if (event.event === 'error') {
          // Check for cancellation error
          const errorType = 'error_type' in event.data ? event.data.error_type : null;
          updatedJob.status = errorType === 'JOB_CANCELLED' ? 'CANCELLED' : 'FAILED';
        }

        updatedJobs = {
          ...state.activeJobs,
          [jobId]: updatedJob,
        };
      }

      return {
        eventsByProject: {
          ...state.eventsByProject,
          [projectId]: updatedEvents,
        },
        activeJobs: updatedJobs,
      };
    });

    // Persist to localStorage
    get().saveToStorage();
  },

  clearProjectEvents: (projectId: string): void => {
    set(state => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _removed, ...remainingEvents } = state.eventsByProject;
      return { eventsByProject: remainingEvents };
    });

    // Persist to localStorage
    get().saveToStorage();
  },

  clearAllEvents: (): void => {
    set({ eventsByProject: {}, activeJobs: {} });

    // Persist to localStorage
    get().saveToStorage();
  },

  // Persistence
  loadFromStorage: (): void => {
    try {
      const stored = getWithTTL<StoredSSEData>(STORAGE_KEY);
      if (!stored) {
        return; // No stored data or expired
      }

      // Validate version
      if (stored.version !== 1) {
        logger.warn({ component: 'sseStore' }, 'Unknown SSE data version, skipping load', {
          version: stored.version,
        });
        return;
      }

      // Clean up stale jobs
      const activeJobs: Record<string, ActiveJob> = {};
      for (const [jobId, job] of Object.entries(stored.activeJobs)) {
        if (!isJobStale(job)) {
          activeJobs[jobId] = job;
        }
      }

      // Load events (TTL handled by getWithTTL)
      // Deduplicate events to clean up any duplicates from previous sessions
      const eventsByProject: Record<string, SSEEvent[]> = {};
      for (const [projectId, data] of Object.entries(stored.events)) {
        const deduplicated = deduplicateEvents(data.events);
        eventsByProject[projectId] = deduplicated;

        if (deduplicated.length < data.events.length) {
          logger.info({ component: 'sseStore' }, 'Cleaned up duplicate events from localStorage', {
            projectId,
            originalCount: data.events.length,
            deduplicatedCount: deduplicated.length,
            removedCount: data.events.length - deduplicated.length,
          });
        }
      }

      set({ activeJobs, eventsByProject });
    } catch (error) {
      logger.error(
        { component: 'sseStore' },
        'Failed to load SSE data from storage',
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  },

  saveToStorage: (): void => {
    try {
      const { activeJobs, eventsByProject } = get();

      const data: StoredSSEData = {
        version: 1,
        activeJobs,
        events: Object.entries(eventsByProject).reduce(
          (acc, [projectId, events]) => {
            acc[projectId] = {
              timestamp: Date.now(),
              events,
            };
            return acc;
          },
          {} as StoredSSEData['events'],
        ),
      };

      setWithTTL(STORAGE_KEY, data, EVENTS_TTL);
    } catch (error) {
      logger.error(
        { component: 'sseStore' },
        'Failed to save SSE data to storage',
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  },
}));

// Load from storage on initialization
if (typeof window !== 'undefined') {
  useSSEStore.getState().loadFromStorage();
}
